import { ActivityEntityName } from '../shared/activity.entity.name';
import { EntityLink } from '../entity';
import { Invoice, InvoiceProcessingType, InvoiceType } from './invoicing';
import { InvoiceStatus } from './invoicing.enum';

/**
 * Create/update allowlist (production contract).
 * Number, status, version, integration, settlement, totals, audit, and provenance are server-owned.
 */
export const INVOICE_MUTABLE_FIELDS = [
  'contact',
  'billingCompany',
  'job',
  'workOrder',
  'invoiceDate',
  'dueDate',
  'customerReference',
  'notes',
  'terms',
  'taxInclusiveOption',
  'taxRate',
  'lines',
  'attachments',
  'theme',
  'pricebook',
  'timezone',
  'type',
  'resources',
  'links',
] as const;

export type InvoiceMutableField = (typeof INVOICE_MUTABLE_FIELDS)[number];

/** Fields clients must never set via generic save/patch. */
export const INVOICE_PROTECTED_FIELDS = [
  'invoiceNumber',
  'status',
  'subStatus',
  'version',
  'integration',
  'integrationId',
  'subTotal',
  'tax',
  'total',
  'prepaymentSubTotal',
  'prepaymentTax',
  'prepaymentTotal',
  'amountPaid',
  'amountRefunded',
  'lastPaymentDate',
  'isReconcile',
  'outstandingAmount',
  'creditAmount',
  'roundingAmount',
  'payments',
  'hasPayments',
  'hasAllocation',
  'copiedFromSource',
  'source',
  'generatedDate',
  'lastIssuedDate',
  'modifiedAfterSend',
  'pdf',
  'timesheetIds',
  'systemTags',
  'createdAt',
  'createdBy',
  'updatedAt',
  'updatedBy',
  'active',
] as const;

export type InvoiceProtectedField = (typeof INVOICE_PROTECTED_FIELDS)[number];

export type InvoiceSubmitIntent = 'saveDraft' | 'approve' | 'approveAndSend';

export const INVOICE_SUBMIT_INTENT_KEY = 'submitIntent';

const INVOICE_VIRTUAL_BINDS = ['recipientCustomer', 'recipientSite', 'recipientContact', '$variables', INVOICE_SUBMIT_INTENT_KEY] as const;

const SERVER_OWNED_INVOICE_LINK_TYPES = new Set<string>([
  ActivityEntityName.Quote,
  ActivityEntityName.QuotePro,
  ActivityEntityName.SaleOrder,
  ActivityEntityName.Invoice,
  ActivityEntityName.PrepaymentInvoice,
  ActivityEntityName.Job,
  ActivityEntityName.WorkOrder,
]);

const IDENTITY_FIELDS = ['_id', 'type', 'contact', 'job', 'workOrder', 'links', 'timezone', 'billingCompany'] as const;

export function listForbiddenInvoicePatchFields(patch: Partial<Invoice> | Record<string, unknown> | null | undefined): string[] {

  if (!patch) return [];
  return INVOICE_PROTECTED_FIELDS.filter(field => Object.prototype.hasOwnProperty.call(patch, field));
}

export function assertInvoicePatchAllowlist(patch: Partial<Invoice> | Record<string, unknown> | null | undefined): void {

  const forbidden = listForbiddenInvoicePatchFields(patch);
  if (forbidden.length > 0) {
    throw new Error(`Invoice patch rejected protected fields: ${forbidden.join(', ')}`);
  }
}

/** Date-typed fields accepted from clients. Wire format is an ISO string (or Date/null). */
export const INVOICE_DATE_FIELDS = ['invoiceDate', 'dueDate'] as const;

/**
 * Coerce ISO strings/numbers to Dates and reject anything uncastable (e.g. a Date instance
 * degraded to `{}` by spread-cloning) so it fails loudly instead of corrupting the write.
 */
export function normalizeInvoiceDates(patch: Partial<Invoice> | null | undefined): Partial<Invoice> {

  const result = { ...(patch ?? {}) } as Record<string, unknown>;
  for (const field of INVOICE_DATE_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(result, field)) continue;
    const value = result[field];
    if (value === null || value === undefined) continue;
    if (value instanceof Date) {
      if (Number.isNaN(value.getTime())) throw new Error(`${field} is not a valid date`);
      continue;
    }
    if (typeof value === 'string' || typeof value === 'number') {
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) throw new Error(`${field} is not a valid date`);
      result[field] = parsed;
      continue;
    }
    throw new Error(`${field} is not a valid date value`);
  }
  return result as Partial<Invoice>;
}

export function pickInvoiceMutableFields(source: Partial<Invoice> | Record<string, unknown> | null | undefined): Partial<Invoice> {

  const result: Partial<Invoice> = {};
  if (!source) return result;

  for (const field of INVOICE_MUTABLE_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(source, field)) {
      (result as Record<string, unknown>)[field] = (source as Record<string, unknown>)[field];
    }
  }
  return normalizeInvoiceDates(result);
}

export function stripInvoiceVirtualBinds(model: Record<string, unknown>): Record<string, unknown> {

  const result = { ...model };
  for (const key of INVOICE_VIRTUAL_BINDS) delete result[key];
  return result;
}

/** Restore mounted identity when Surface bind projection omitted it. Explicit nulls stay null. */
export function mergeMountedInvoiceIdentity(submitted: Record<string, unknown>, mounted: Record<string, unknown> | null | undefined): Record<string, unknown> {

  const source = mounted ?? {};
  const result = { ...submitted };
  for (const key of IDENTITY_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(submitted, key)) continue;
    if (source[key] != null && source[key] !== '') result[key] = source[key];
  }
  return result;
}

export function readInvoiceSubmitIntent(model: Record<string, unknown> | null | undefined): InvoiceSubmitIntent {

  const vars = model?.['$variables'] as Record<string, unknown> | undefined;
  const raw = vars?.[INVOICE_SUBMIT_INTENT_KEY] ?? vars?.['invoiceSubmitIntent'] ?? model?.[INVOICE_SUBMIT_INTENT_KEY];
  if (raw === 'approveAndSend' || raw === InvoiceProcessingType.ApprovedAndSend) return 'approveAndSend';
  if (raw === 'approve' || raw === InvoiceProcessingType.Approved) return 'approve';
  return 'saveDraft';
}

export function toInvoiceProcessingType(intent: InvoiceSubmitIntent): InvoiceProcessingType {

  if (intent === 'approveAndSend') return InvoiceProcessingType.ApprovedAndSend;
  if (intent === 'approve') return InvoiceProcessingType.Approved;
  return InvoiceProcessingType.Draft;
}

/** Draft is fully editable; Approved is conditional (Manage Approved Invoices — 10492). */
export function isInvoiceEditable(status: InvoiceStatus | undefined | null): boolean {

  return status == null || status === InvoiceStatus.Draft;
}

export function assertInvoiceEditable(status: InvoiceStatus | undefined | null): void {

  if (!isInvoiceEditable(status)) {
    throw new Error(`Invoice is not editable in status ${status}`);
  }
}

/** Draft disposal is Void, not Delete. */
export function canDeleteInvoice(_status?: InvoiceStatus | undefined | null): boolean {

  return false;
}

export function assertInvoiceDeletable(_status?: InvoiceStatus | undefined | null): never {

  throw new Error('Invoice draft disposal uses Void, not Delete');
}

/** Quote / Sales Order / Invoice / Job / Stage lineage stays server-owned on generic save/patch. */
export function sanitizeInvoiceClientLinks(links: EntityLink[] | undefined, existing?: EntityLink[]): EntityLink[] {

  const preserved = (existing ?? []).filter(isServerOwnedInvoiceLink);
  const clientLinks = (links ?? []).filter(link => !isServerOwnedInvoiceLink(link));
  return [...clientLinks, ...preserved];
}

export function isServerOwnedInvoiceLink(link: EntityLink | null | undefined): boolean {

  return !!link?.type && SERVER_OWNED_INVOICE_LINK_TYPES.has(link.type);
}

const LINKED_LINE_LOCKED_FIELDS = ['name', 'quantity', 'price', 'itemLink', 'catalogueCodeId', 'uom', 'codeUoM'] as const;

export function assertInvoiceWriteInvariants(invoice: Partial<Invoice> | null | undefined): void {

  if (!invoice) return;
  const invoiceDate = invoice.invoiceDate ? new Date(invoice.invoiceDate as Date) : null;
  const dueDate = invoice.dueDate ? new Date(invoice.dueDate as Date) : null;
  if (invoiceDate && Number.isNaN(invoiceDate.getTime())) throw new Error('invoiceDate is not a valid date');
  if (dueDate && Number.isNaN(dueDate.getTime())) throw new Error('dueDate is not a valid date');
  if (invoiceDate && dueDate && dueDate.getTime() < invoiceDate.getTime()) {
    throw new Error('Due date cannot precede invoice date');
  }
}

/** Billing company is identity after create. Same id is a no-op; a swap is rejected. */
export function assertInvoiceBillingCompanyUnchanged(patch: Partial<Invoice> | Record<string, unknown> | null | undefined, current: Partial<Invoice> | Record<string, unknown> | null | undefined): void {

  if (!patch || !Object.prototype.hasOwnProperty.call(patch, 'billingCompany')) return;
  const currentId = invoiceBillingCompanyId(current?.['billingCompany']);
  if (!currentId) return;
  if (invoiceBillingCompanyId(patch['billingCompany']) === currentId) return;
  throw new Error('Billing company cannot be changed after the invoice is created');
}

function invoiceBillingCompanyId(value: unknown): string {

  if (typeof value === 'string') return value;
  if (value && typeof value === 'object' && typeof (value as { _id?: unknown })._id === 'string') {
    return (value as { _id: string })._id;
  }
  return '';
}

/**
 * Create payloads omit server-owned `total`. Treat missing total as the sum of line totals
 * so `undefined >= 0` cannot reject a valid Finance-Pro draft.
 */
export function invoiceHasNonNegativeMoney(invoice: Partial<Invoice> | null | undefined): boolean {

  if (!invoice) return false;
  const lines = invoice.lines ?? [];
  const isCreditNote = invoice.type === InvoiceType.CreditNote;
  if (!isCreditNote && lines.some(line => (line.price ?? 0) < 0)) return false;
  const total = typeof invoice.total === 'number'
    ? invoice.total
    : lines.reduce((sum, line) => sum + Number(line.total ?? (line.price ?? 0) * (line.quantity ?? 1)), 0);
  return isCreditNote || total >= 0;
}

export function sanitizeInvoiceLinkedLines(next: Invoice['lines'] | undefined, existing?: Invoice['lines']): Invoice['lines'] | undefined {

  if (!next) return next;
  const previous = new Map((existing ?? []).filter(line => line?._id).map(line => [line._id, line]));
  return next.map(line => {
    const prior = previous.get(line._id);
    if (!prior?.itemLink) return line;
    const locked = { ...line };
    for (const field of LINKED_LINE_LOCKED_FIELDS) {
      (locked as unknown as Record<string, unknown>)[field] = (prior as unknown as Record<string, unknown>)[field];
    }
    return locked;
  });
}
